import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertTriangle, Check, Share, Plus, Smartphone, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { renderSafeMarkup } from '../utils/safeMarkup';

// Show iOS Safari users a small install hint banner.
// True only for: iPhone/iPad + Safari + not yet running as installed PWA + not previously dismissed.
const isIosSafariInBrowser = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
  const isStandalone = window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
  return isIos && isSafari && !isStandalone;
};

export const LoginPage = () => {
  const navigate = useNavigate();
  const { user, loading, isLocalMode, enterLocalMode } = useAuth();
  const { t } = useLanguage();
  const [showLocalWarning, setShowLocalWarning] = useState(false);
  const [acks, setAcks] = useState([false, false, false, false, false]);
  const [showIosHint, setShowIosHint] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (localStorage.getItem('ios_install_hint_dismissed') === 'true') return false;
    return isIosSafariInBrowser();
  });

  const dismissIosHint = () => {
    localStorage.setItem('ios_install_hint_dismissed', 'true');
    setShowIosHint(false);
  };

  const warningKeys = ['localWarning1', 'localWarning2', 'localWarning3', 'localWarning4', 'localWarning5'];

  const allAcked = acks.every(Boolean);

  const toggleAck = (i) => {
    setAcks(prev => { const next = [...prev]; next[i] = !next[i]; return next; });
  };

  useEffect(() => {
    if (!loading && (user || isLocalMode)) {
      navigate('/library', { replace: true });
    }
  }, [user, loading, isLocalMode, navigate]);

  // One-shot guidance after a "switch accounts" logout — fires if the user
  // hit back-button → "Sign out" on the library page, which sets this flag.
  useEffect(() => {
    if (sessionStorage.getItem('force_account_picker') === 'true') {
      sessionStorage.removeItem('force_account_picker');
      toast.info(
        'Signed out. To use a different Google account, pick "Use another account" on the next screen, or sign out of Google in another tab first.',
        { duration: 8000, id: 'switch-account-hint' }
      );
    }
  }, []);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/library';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLocalMode = () => {
    enterLocalMode();
    navigate('/library', { replace: true });
  };

  return (
    <main className="min-h-screen flex items-center justify-center p-4 overflow-x-hidden" style={{ backgroundColor: 'hsl(82, 55%, 95%)' }}>
      <div className="max-w-md w-full">
        {/* iOS install hint — Safari + iPhone only, dismissible */}
        {showIosHint && (
          <div
            className="mb-6 rounded-2xl p-4 shadow-sm relative"
            style={{ backgroundColor: 'white', border: '1px solid hsl(143, 28%, 85%)' }}
            data-testid="ios-install-hint"
            role="region"
            aria-label="Install on iPhone"
          >
            <button
              onClick={dismissIosHint}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100"
              aria-label="Dismiss install hint"
              data-testid="ios-install-hint-dismiss"
            >
              <X className="w-4 h-4" style={{ color: 'hsl(125, 12%, 35%)' }} />
            </button>
            <div className="flex items-start gap-3 pr-6">
              <div
                className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center"
                style={{ backgroundColor: 'hsl(82, 55%, 95%)' }}
                aria-hidden="true"
              >
                <Smartphone className="w-5 h-5" style={{ color: 'hsl(143, 28%, 35%)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold mb-1" style={{ color: 'hsl(143, 28%, 25%)' }}>
                  Install on iPhone
                </p>
                <p className="text-xs leading-relaxed" style={{ color: 'hsl(125, 12%, 35%)' }}>
                  Tap{' '}
                  <Share className="inline w-3.5 h-3.5 align-text-bottom" aria-label="Share" />{' '}
                  <span className="font-medium">Share</span> at the bottom of Safari, then{' '}
                  <Plus className="inline w-3.5 h-3.5 align-text-bottom" aria-label="Plus" />{' '}
                  <span className="font-medium">Add to Home Screen</span>.
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 mb-6">
            <img src="/monstera-login-v3.png" alt="Monstera leaf" width="80" height="80" style={{ objectFit: 'contain' }} />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4" style={{ color: 'hsl(143, 28%, 25%)' }}>
            {t('appName')}
          </h1>
          <p className="text-lg" style={{ color: 'hsl(125, 12%, 25%)' }}>
            {t('loginSubtitle1')}<br />
            {t('loginSubtitle2')}
          </p>
        </div>

        {/* Login Card */}
        <div className="rounded-3xl p-8 shadow-lg" style={{ backgroundColor: 'white', border: '1px solid hsl(143, 28%, 90%)' }}>
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold mb-2" style={{ color: 'hsl(143, 28%, 25%)' }}>
                {t('getStarted')}
              </h2>
              <p className="text-sm" style={{ color: 'hsl(125, 12%, 32%)' }}>
                {t('loginSignInDesc')}
              </p>
            </div>

            <Button
              onClick={handleLogin}
              className="w-full rounded-full h-12 text-base font-medium shadow-lg"
              style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'hsl(115, 56%, 96%)', transition: 'all 0.2s ease' }}
              data-testid="google-login-button"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('continueWithGoogle')}
            </Button>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px" style={{ backgroundColor: 'hsl(143, 28%, 90%)' }} />
              <span className="text-xs" style={{ color: 'hsl(125, 12%, 42%)' }}>{t('or')}</span>
              <div className="flex-1 h-px" style={{ backgroundColor: 'hsl(143, 28%, 90%)' }} />
            </div>

            {/* Local mode */}
            {!showLocalWarning ? (
              <button
                onClick={() => { setShowLocalWarning(true); setAcks([false, false, false, false, false]); }}
                className="w-full text-sm py-2 rounded-full border transition-colors hover:bg-gray-50"
                style={{ color: 'hsl(125, 12%, 28%)', borderColor: 'hsl(143, 28%, 85%)' }}
                data-testid="use-locally-button"
              >
                {t('idRatherNot')}
              </button>
            ) : (
              <div className="rounded-xl p-4 space-y-3" style={{ backgroundColor: 'hsl(40, 90%, 96%)', border: '1px solid hsl(40, 90%, 80%)' }} data-testid="local-mode-warning">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'hsl(40, 90%, 40%)' }} />
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'hsl(40, 50%, 18%)' }}>
                      {t('onDeviceStorage')}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'hsl(40, 30%, 35%)' }}>
                      {t('acknowledgeEach')}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  {warningKeys.map((key, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2 rounded-lg p-2 transition-colors"
                      style={{ backgroundColor: acks[i] ? 'hsl(143, 30%, 95%)' : 'white', border: `1px solid ${acks[i] ? 'hsl(143, 28%, 80%)' : 'hsl(40, 50%, 85%)'}` }}
                    >
                      <div
                        className="flex-1 text-xs leading-relaxed pt-0.5"
                        style={{ color: acks[i] ? 'hsl(143, 28%, 30%)' : 'hsl(40, 30%, 25%)' }}
                      >
                        {renderSafeMarkup(t(key))}
                      </div>
                      <button
                        onClick={() => toggleAck(i)}
                        className="flex-shrink-0 flex items-center justify-center rounded-md text-xs font-semibold transition-all"
                        style={{
                          width: '44px',
                          height: '26px',
                          backgroundColor: acks[i] ? 'hsl(143, 40%, 42%)' : 'white',
                          color: acks[i] ? 'white' : 'hsl(40, 40%, 30%)',
                          border: acks[i] ? 'none' : '1px solid hsl(40, 50%, 75%)',
                        }}
                        data-testid={`ack-btn-${i}`}
                      >
                        {acks[i] ? <Check className="w-3.5 h-3.5" /> : 'ok'}
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 pt-1">
                  <Button
                    onClick={() => setShowLocalWarning(false)}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    data-testid="local-mode-cancel"
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    onClick={handleLocalMode}
                    size="sm"
                    className="flex-1"
                    disabled={!allAcked}
                    style={{
                      backgroundColor: allAcked ? 'hsl(143, 40%, 42%)' : 'hsl(0, 0%, 85%)',
                      color: allAcked ? 'white' : 'hsl(0, 0%, 55%)',
                      cursor: allAcked ? 'pointer' : 'not-allowed',
                    }}
                    data-testid="local-mode-confirm"
                  >
                    {allAcked ? t('continueToLocalMode') : `${t('acknowledgeAll')} (${acks.filter(Boolean).length}/5)`}
                  </Button>
                </div>
              </div>
            )}

            <div className="flex justify-center gap-4 flex-wrap">
              <Link to="/about" className="text-sm underline hover:opacity-80 transition-opacity" style={{ color: 'hsl(125, 15%, 32%)' }} data-testid="login-about-link">
                {t('aboutAndInstructions')}
              </Link>
              <span className="text-sm" style={{ color: 'hsl(125, 12%, 50%)' }}>|</span>
              <Link to="/terms" className="text-sm underline hover:opacity-80 transition-opacity" style={{ color: 'hsl(125, 15%, 32%)' }} data-testid="login-tos-link">
                {t('termsOfService')}
              </Link>
              <span className="text-sm" style={{ color: 'hsl(125, 12%, 50%)' }}>|</span>
              <Link to="/privacy" className="text-sm underline hover:opacity-80 transition-opacity" style={{ color: 'hsl(125, 15%, 32%)' }} data-testid="login-privacy-policy-link">
                {t('privacyPolicy')}
              </Link>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-12 grid grid-cols-3 gap-4 text-center">
          {[
            { icon: '📸', label: 'aiIdentify', desc: 'aiIdentifyDesc' },
            { icon: '☁️', label: 'cloudSync', desc: 'cloudSyncDesc' },
            { icon: '💚', label: 'careTips', desc: 'careTipsDesc' },
          ].map(({ icon, label, desc }) => (
            <div key={label} className="group relative cursor-default" data-testid={`feature-${label}`}>
              <div className="text-2xl font-bold font-display" style={{ color: 'hsl(143, 28%, 25%)' }}>{icon}</div>
              <p className="text-xs mt-1" style={{ color: 'hsl(125, 12%, 32%)' }}>{t(label)}</p>
              <div
                className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 max-w-[calc(100vw-1rem)] rounded-lg px-3 py-2 text-xs leading-snug shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50"
                style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
              >
                {t(desc)}
                <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px]" style={{ borderTopColor: 'hsl(143, 28%, 25%)' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};
